import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { AccessToken } from "@/lib/api";

import { hasAuthTokenExpired } from "./utils";

export type AuthStore = {
  isAuthenticated: boolean;
  token?: AccessToken;
  actions: {
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setAuthToken: (token: AccessToken) => void;
    clearAuthToken: () => void;
  };
};

const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      actions: {
        setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
        clearAuthToken: () => set({ token: undefined, isAuthenticated: false }),
        setAuthToken: (token) =>
          set({ token, isAuthenticated: !hasAuthTokenExpired(token) }),
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) =>
        Object.fromEntries(Object.entries(state).filter(([key]) => key !== "actions")),
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage() {
        return (state) => {
          if (!state) return;

          if (hasAuthTokenExpired(state.token)) {
            state.actions.clearAuthToken();
          }
        };
      },
    },
  ),
);

const authTokenSelector = (state: AuthStore) => state.token;
const isAuthenticatedSelector = (state: AuthStore) => state.isAuthenticated;

export const getAuthToken = () => authTokenSelector(useAuthStore.getState());
export const getIsAuthenticated = () => isAuthenticatedSelector(useAuthStore.getState());

export const useAuthToken = () => useAuthStore(authTokenSelector);
export const useIsAuthenticated = () => useAuthStore(isAuthenticatedSelector);
export const useAuthStoreActions = () => useAuthStore((state) => state.actions);
